import {
  makeStyles,
  TableBody,
  TableFooter,
  TableRow
} from "@material-ui/core";
import TableCell from "@material-ui/core/TableCell";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import StatusLabel from "@saleor/components/StatusLabel";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { SubscriptionList_subscriptions_edges_node } from "@saleor/subscribers/types/SubscriptionList";
import { SubscriberListUrlSortField } from "@saleor/subscribers/urls";
import { ListActions, ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

export interface SubscriberListProps
  extends ListProps,
    ListActions,
    SortPage<SubscriberListUrlSortField> {
  subscribers: SubscriptionList_subscriptions_edges_node[];
}

const numberOfColumns = 13;

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      colName: {
        width: "auto"
      },
      colID: {
        width: 80
      },
      colEmail: {
        width: "auto"
      },
      colActive: {
        width: 180
      },
      colDate: {
        width: 200
      }
    }
  }),
  { name: "SubscriberList" }
);

const SubscriberList: React.FC<SubscriberListProps> = ({
  subscribers,
  disabled,
  isChecked,
  onNextPage,
  onPreviousPage,
  onRowClick,
  pageInfo,
  selected,
  sort,
  toggle,
  toggleAll,
  toolbar,
  onSort
}) => {
  const classes = useStyles({});
  const intl = useIntl();
  return (
    <ResponsiveTable>
      <TableHead
        colSpan={numberOfColumns}
        selected={selected}
        disabled={disabled}
        items={subscribers}
        toggleAll={toggleAll}
        toolbar={toolbar}
      >
        <TableCellHeader
          className={classes.colName}
          direction={
            sort.sort === SubscriberListUrlSortField.name
              ? getArrowDirection(sort.asc)
              : undefined
          }
          arrowPosition="right"
          onClick={() => onSort(SubscriberListUrlSortField.name)}
        >
          <FormattedMessage defaultMessage="ID" />
        </TableCellHeader>
        <TableCellHeader
          className={classes.colName}
          colSpan={3}
          direction={
            sort.sort === SubscriberListUrlSortField.email
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(SubscriberListUrlSortField.email)}
        >
          <FormattedMessage
            defaultMessage="Email"
            description="Email Address"
          />
        </TableCellHeader>
        <TableCellHeader className={classes.colName} colSpan={2}>
          <FormattedMessage
            defaultMessage="Subscribed"
            description="Active Status"
          />
        </TableCellHeader>
        <TableCellHeader
          className={classes.colName}
          colSpan={3}
          direction={
            sort.sort === SubscriberListUrlSortField.subscribed
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(SubscriberListUrlSortField.subscribed)}
        >
          <FormattedMessage
            defaultMessage="Subscribed On"
            description="Subscribed Date"
          />
        </TableCellHeader>
        <TableCellHeader
          className={classes.colName}
          colSpan={3}
          direction={
            sort.sort === SubscriberListUrlSortField.unsubscribed
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(SubscriberListUrlSortField.unsubscribed)}
        >
          <FormattedMessage
            defaultMessage="Unsubscribed On"
            description="Unsubscribed Date"
          />
        </TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          subscribers,
          subscriber => {
            const isSelected = subscriber ? isChecked(subscriber.id) : false;

            return (
              <TableRow
                selected={isSelected}
                hover={!!subscriber}
                key={subscriber ? subscriber.id : "skeleton"}
                onClick={subscriber && onRowClick(subscriber.id)}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    disabled={disabled}
                    disableClickPropagation
                    onChange={() => toggle(subscriber.id)}
                  />
                </TableCell>
                <TableCell className={classes.colID}>
                  {subscriber ? subscriber.subscriptionId : <Skeleton />}
                </TableCell>
                <TableCell className={classes.colEmail} colSpan={3}>
                  {subscriber ? subscriber.email : <Skeleton />}
                </TableCell>
                <TableCell
                  className={classes.colActive}
                  data-test="Active"
                  data-test-active={maybe(() => subscriber.active)}
                  colSpan={2}
                >
                  {subscriber &&
                  maybe(() => subscriber.active !== undefined) ? (
                    <StatusLabel
                      label={
                        subscriber.active
                          ? intl.formatMessage({
                              defaultMessage: "Active",
                              description: "subscriber status"
                            })
                          : intl.formatMessage({
                              defaultMessage: "Inactive",
                              description: "subscriber status"
                            })
                      }
                      status={subscriber.active ? "success" : "error"}
                    />
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colColor}
                  align="left"
                  colSpan={3}
                >
                  {subscriber ? subscriber.created : <Skeleton />}
                </TableCell>
                <TableCell
                  className={classes.colCreated}
                  align="left"
                  colSpan={3}
                >
                  {subscriber ? subscriber.unsubscribed : <Skeleton />}
                </TableCell>
              </TableRow>
            );
          },
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No Subscriber found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};

export default SubscriberList;
